import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";

import Dialog from "src/components/Dialog";
import Autocomplete from "src/components/Autocomplete";
import LoadableButton from "src/components/LoadableButton";
import { AddGeneralLedgerAccountDialog } from "src/pages/RootDialogs/AddGeneralLedgerAccountDialog";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import type { GeneralLedgerAccountV2 } from "src/interfaces/generalLedgerAccount.interface";
import { useRecategorizeDialogStyles } from "./styles";

// Account subtypes that can't be assigned in bulk
const DISABLED_SUBTYPES = [
  "credit-card",
  "bank-account-cash",
  "loan-line-of-credit",
];

const isDisabledAccount = (account: GeneralLedgerAccountV2) => {
  const subType = account.type?.[1];
  return DISABLED_SUBTYPES.includes(subType as string);
};

interface RecategorizeDialogProps {
  open: boolean;
  transactionCount: number;
  currentAccountId: string;
  onClose: () => void;
  onConfirm: (accountId: string, accountName: string) => void;
  loading?: boolean;
}

type ContentProps = Omit<RecategorizeDialogProps, "open">;

function RecategorizeDialogContent({
  transactionCount,
  currentAccountId,
  onClose,
  onConfirm,
  loading = false,
}: ContentProps) {
  const classes = useRecategorizeDialogStyles();
  const [selectedAccount, setSelectedAccount] =
    useState<GeneralLedgerAccountV2 | null>(null);
  const [addAccountDialogOpen, setAddAccountDialogOpen] = useState(false);

  const generalLedgerAccounts = useSelector(generalLedgerAccountsSelector);

  const options = useMemo(() => {
    let balanceTransfer: GeneralLedgerAccountV2 | undefined;

    const accounts = [...generalLedgerAccounts].filter((account) => {
      if (!account) return false;
      // Filter out current account
      if (account.id === currentAccountId) return false;
      if (account.id === "transfers") {
        balanceTransfer = account;
        return false;
      }
      if (account.system && account.id !== "sales-tax-payable") return false;
      return true;
    });

    // Sort by type (expenses first, then income, assets, liabilities, equity) then name
    const typeOrder: Record<string, number> = {
      expenses: 1,
      income: 2,
      assets: 3,
      liabilities: 4,
      equity: 5,
    };
    accounts.sort(
      (a, b) =>
        (typeOrder[a.type[0]] || 99) - (typeOrder[b.type[0]] || 99) ||
        a.name.localeCompare(b.name)
    );

    accounts.unshift({
      name: "Add New Account",
      type: ["new"],
      id: "add_new_account",
    } as any);

    if (balanceTransfer) {
      accounts.unshift(balanceTransfer);
    }

    return accounts;
  }, [generalLedgerAccounts, currentAccountId]);

  const handleConfirm = () => {
    if (selectedAccount) {
      onConfirm(selectedAccount.id, selectedAccount.name);
    }
  };

  const handleAccountChange = (
    _event: React.ChangeEvent<{}>,
    value: GeneralLedgerAccountV2 | null
  ) => {
    if (value?.name === "Add New Account") {
      setAddAccountDialogOpen(true);
      return;
    }
    setSelectedAccount(value);
  };

  const handleAddAccountClose = (account?: GeneralLedgerAccountV2) => {
    setAddAccountDialogOpen(false);
    if (account) {
      setSelectedAccount(account);
    }
  };

  return (
    <>
      <AddGeneralLedgerAccountDialog
        open={addAccountDialogOpen}
        onClose={handleAddAccountClose}
      />
      <DialogTitle>Recategorize Transactions</DialogTitle>
      <DialogContent className={classes.content}>
        <DialogContentText className={classes.subtitle}>
          Select a new category for {transactionCount} transaction
          {transactionCount !== 1 ? "s" : ""}.
        </DialogContentText>
        <Autocomplete
          fullWidth
          autoHighlight
          options={options}
          value={selectedAccount}
          onChange={handleAccountChange}
          getOptionLabel={(option) => option?.name || ""}
          getOptionDisabled={(option) => isDisabledAccount(option)}
          groupBy={(option) => {
            if (option.id === "add_new_account" || option.id === "transfers")
              return "";
            const type = option.type?.[0];
            if (type === "expenses") return "Expenses";
            if (type === "income") return "Income";
            if (type === "assets") return "Assets";
            if (type === "liabilities") return "Liabilities";
            if (type === "equity") return "Equity";
            return "Other";
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select a category..."
              variant="outlined"
              size="small"
              autoFocus
            />
          )}
          renderOption={(option) => {
            const disabled = isDisabledAccount(option);
            if (disabled) {
              return (
                <Tooltip
                  title="These accounts currently can't be assigned in bulk."
                  placement="right"
                  PopperProps={{
                    style: { pointerEvents: "none" },
                  }}
                >
                  <div className={classes.disabledOption}>{option.name}</div>
                </Tooltip>
              );
            }
            return <span>{option.name}</span>;
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadableButton
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!selectedAccount}
          loading={loading}
        >
          Recategorize
        </LoadableButton>
      </DialogActions>
    </>
  );
}

function RecategorizeDialog({
  open,
  ...contentProps
}: RecategorizeDialogProps) {
  return (
    <Dialog open={open} onClose={contentProps.onClose} fullWidth maxWidth="xs">
      {open && <RecategorizeDialogContent {...contentProps} />}
    </Dialog>
  );
}

export default RecategorizeDialog;
